import { useCallback, useEffect, useRef, useState } from 'react';
import { CompanySummary, findCompany, findCompanyById, searchCompanies } from './companies';
import { describeError, textSearchable } from './payPolicyTemplates/api';
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from './useDebouncedValue';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ID_RE = /^\d+$/;

export type CompanyLookup = 'uuid' | 'id' | 'name';

/** How the text is looked up: a full uuid exactly, digits as an id when ids are searched, else from three characters, spaces kept ("ny "), by name. */
export const companyLookupOf = (text: string, byId = false): CompanyLookup | null => {
  const trimmed = text.trim();
  if (UUID_RE.test(trimmed)) return 'uuid';
  if (byId && ID_RE.test(trimmed)) return 'id';
  return textSearchable(text) ? 'name' : null;
};

interface FoundPage {
  companies: CompanySummary[];
  total: number | null;
  exact: boolean;
}

export const useCompanyMatches = (input: string, { byId = false }: { byId?: boolean } = {}) => {
  const debounced = useDebouncedValue(input, SEARCH_DEBOUNCE_MS);
  const lookup = companyLookupOf(debounced, byId);
  const [matches, setMatches] = useState<CompanySummary[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [exact, setExact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  // Scroll events come faster than renders: the loading state cannot stop a second request for the same page.
  const pageInFlight = useRef(false);

  const fetchPage = useCallback(
    (offset: number) => {
      generation.current += offset === 0 ? 1 : 0;
      const mine = generation.current;
      const term = debounced.trim();
      setLoading(true);
      setError(null);
      const find = async (): Promise<FoundPage> => {
        if (offset === 0 && lookup === 'uuid') {
          const company = await findCompany(term.toLowerCase());
          return { companies: company ? [company] : [], total: company ? 1 : 0, exact: true };
        }
        if (offset === 0 && lookup === 'id') {
          const company = await findCompanyById(Number(term));
          // A name made of digits is still found by name when no company has that id.
          if (company || !textSearchable(debounced)) return { companies: company ? [company] : [], total: company ? 1 : 0, exact: true };
        }
        return { ...(await searchCompanies(debounced, offset)), exact: false };
      };
      find()
        .then((page) => {
          if (mine !== generation.current) return;
          setMatches((previous) => {
            if (offset === 0) return page.companies;
            const known = new Set(previous.map((company) => company.uuid));
            return [...previous, ...page.companies.filter((company) => !known.has(company.uuid))];
          });
          setTotal(page.total);
          setExact(page.exact);
        })
        .catch((caught) => {
          if (mine === generation.current) setError(describeError(caught));
        })
        .finally(() => {
          pageInFlight.current = false;
          if (mine === generation.current) setLoading(false);
        });
    },
    [debounced, lookup]
  );

  useEffect(() => {
    if (!lookup) {
      generation.current += 1;
      setMatches([]);
      setTotal(null);
      setExact(false);
      setError(null);
      setLoading(false);
      return;
    }
    fetchPage(0);
  }, [lookup, fetchPage]);

  const hasMore = !exact && total !== null && matches.length < total;
  const loadMore = useCallback(() => {
    if (!hasMore || pageInFlight.current) return;
    pageInFlight.current = true;
    fetchPage(matches.length);
  }, [hasMore, fetchPage, matches.length]);

  return { matches, total, hasMore, loadMore, loading, error, settled: debounced === input, byUuid: lookup === 'uuid', exact };
};
